import { z } from 'zod';

export const LOCALE_LANGUAGE_MAX_LENGTH = 16;
export const TIMEZONE_OVERRIDE_MAX_LENGTH = 64;

export const SUPPORTED_LOCALE_LANGUAGES = [
  'en',
  'en-US',
  'en-GB',
  'fr',
  'de',
  'es',
  'pt-BR',
  'ja',
] as const;

export const localeLanguageSchema = z
  .enum(SUPPORTED_LOCALE_LANGUAGES)
  .refine((value) => value.length <= LOCALE_LANGUAGE_MAX_LENGTH);

export type LocaleLanguage = z.infer<typeof localeLanguageSchema>;

const timezoneNameSchema = z
  .string()
  .min(1)
  .max(TIMEZONE_OVERRIDE_MAX_LENGTH)
  .refine(isSupportedTimeZone, 'must be a supported IANA time zone name');

export const timezoneOverrideSchema = timezoneNameSchema.nullable();

export type TimezoneOverride = z.infer<typeof timezoneOverrideSchema>;

export const localePreferencesSchema = z.object({
  language: localeLanguageSchema,
  timezoneOverride: timezoneOverrideSchema,
});

export type LocalePreferences = z.infer<typeof localePreferencesSchema>;

export const localeLanguagePatchSchema = z.strictObject({
  language: localeLanguageSchema,
});

export type LocaleLanguagePatch = z.infer<typeof localeLanguagePatchSchema>;

export const localeTimezonePatchSchema = z.strictObject({
  timezoneOverride: timezoneOverrideSchema,
});

export type LocaleTimezonePatch = z.infer<typeof localeTimezonePatchSchema>;

export const DEFAULT_LOCALE_PREFERENCES = {
  language: 'en',
  timezoneOverride: null,
} satisfies LocalePreferences;

const dateFieldSchema = z.enum(['day', 'month', 'year']);

export const localeDateTimePartsSchema = z.object({
  formatted: z.string(),
  dateFieldOrder: z.array(dateFieldSchema).length(3),
  year: z.number().int(),
  month: z.number().int().min(1).max(12),
  day: z.number().int().min(1).max(31),
  hour: z.number().int().min(0).max(24),
  minute: z.number().int().min(0).max(59),
  dayPeriod: z.string().nullable(),
});

export type LocaleDateTimeParts = z.infer<typeof localeDateTimePartsSchema>;

export function formatLocaleDateTime(
  instant: string | number | Date,
  preferences: LocalePreferences,
): string {
  const date = toDate(instant);

  return dateTimeFormatter(preferences).format(date);
}

export function formatLocaleDateTimeParts(
  instant: string | number | Date,
  preferences: LocalePreferences,
): LocaleDateTimeParts {
  const date = toDate(instant);
  const formatter = dateTimeFormatter(preferences);
  const parts = formatter.formatToParts(date);

  return localeDateTimePartsSchema.parse({
    formatted: formatter.format(date),
    dateFieldOrder: dateFieldOrder(parts),
    year: numberPart(parts, 'year'),
    month: numberPart(parts, 'month'),
    day: numberPart(parts, 'day'),
    hour: numberPart(parts, 'hour'),
    minute: numberPart(parts, 'minute'),
    dayPeriod: stringPart(parts, 'dayPeriod'),
  });
}

function dateTimeFormatter(preferences: LocalePreferences): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(preferences.language, {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: preferences.timezoneOverride ?? 'UTC',
  });
}

function toDate(instant: string | number | Date): Date {
  const date = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(date.getTime())) {
    throw new RangeError('Invalid instant');
  }
  return date;
}

function numberPart(parts: Intl.DateTimeFormatPart[], type: string): number {
  const value = parts.find((part) => part.type === type)?.value;
  if (value === undefined) {
    throw new RangeError(`Missing ${type} date-time part`);
  }
  return Number(value);
}

function stringPart(parts: Intl.DateTimeFormatPart[], type: string): string | null {
  return parts.find((part) => part.type === type)?.value ?? null;
}

function dateFieldOrder(
  parts: Intl.DateTimeFormatPart[],
): z.infer<typeof localeDateTimePartsSchema>['dateFieldOrder'] {
  const order: z.infer<typeof dateFieldSchema>[] = [];
  for (const part of parts) {
    const parsed = dateFieldSchema.safeParse(part.type);
    if (parsed.success && !order.includes(parsed.data)) {
      order.push(parsed.data);
    }
  }
  return order;
}

function isSupportedTimeZone(value: string): boolean {
  if (value === 'UTC') {
    return true;
  }

  const supportedValuesOf = Intl.supportedValuesOf;
  if (typeof supportedValuesOf === 'function') {
    return supportedValuesOf('timeZone').includes(value);
  }

  try {
    new Intl.DateTimeFormat('en', { timeZone: value }).format(new Date(0));
    return true;
  } catch {
    return false;
  }
}
